"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, getAuthToken } from "@/components/auth-provider";
import { secureFetch } from "@/lib/security/fetch-client";

// 全局关注状态缓存: keyword → following
// 解决同一关键词在不同位置(列表/侧边栏/详情页)状态不同步的问题。
// 导出供 use-follow-celebrity 复用（共享缓存和请求去重，避免 N 个 hook 独立发请求）。
export const followCache = new Map<string, boolean>();
const listeners = new Set<() => void>();

// 请求去重：避免多个 hook 同时触发相同的 GET /api/subscriptions
let pendingFetch: Promise<Map<string, boolean>> | null = null;

/**
 * 异步批量通知：用 queueMicrotask 调度，让 React 能在同一批处理中
 * 合并多个 setState，避免 ~150 个 AffinityChip 同时 setState 导致
 * React #185 (Maximum update depth exceeded)。
 */
let notifyScheduled = false;
export function scheduleNotifyListeners() {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => {
    notifyScheduled = false;
    listeners.forEach((fn) => fn());
  });
}

/** 同步通知（仅用于 invalidateFollowCache 等需要立即生效的场景） */
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function subscribeFollowCache(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** 使缓存中的某个关键词失效(外部删除后调用) */
export function invalidateFollowCache(keyword: string) {
  followCache.delete(keyword);
  notifyListeners();
}

/**
 * 获取用户全部订阅列表（去重：多个 hook 共享同一个 Promise）。
 * 成功后批量写入 followCache，后续 hook 直接读缓存，不再发请求。
 */
function fetchAllSubscriptions(): Promise<Map<string, boolean>> {
  if (pendingFetch) return pendingFetch;

  pendingFetch = (async () => {
    try {
      const token = getAuthToken();
      if (!token) return followCache;

      const data = await secureFetch<{ subscriptions: Array<{ keyword: string; type: number }> }>(
        "/api/subscriptions",
        { method: "GET", headers: { Authorization: `Bearer ${token}` } }
      );
      const subs = data?.subscriptions || [];
      for (const s of subs) {
        followCache.set(s.keyword, true);
      }
      scheduleNotifyListeners();
      return followCache;
    } catch {
      return followCache;
    } finally {
      pendingFetch = null;
    }
  })();

  return pendingFetch;
}

interface FollowState {
  /** 是否已关注 */
  following: boolean;
  /** 切换关注/取关，返回是否成功 */
  toggle: () => Promise<boolean>;
  /** 请求中 */
  loading: boolean;
}

/**
 * 通用关键词关注 hook。
 * 关注 = 把关键词写入现有订阅表(type=0)。
 * 复用 /api/subscriptions (GET/POST/DELETE)。
 *
 * 使用全局缓存保证同一关键词在不同位置的"已关注"状态同步。
 * 首次加载时只发一次 GET /api/subscriptions，结果共享给所有 hook。
 */
export function useFollowKeyword(keyword: string): FollowState {
  const { user } = useAuth();
  const [following, setFollowing] = useState(() => followCache.get(keyword) ?? false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(() => followCache.has(keyword));
  const followingRef = useRef(following);
  followingRef.current = following;

  // 监听全局缓存变化（仅值真正变化时才 setState，避免冗余渲染触发 #185）
  useEffect(() => {
    return subscribeFollowCache(() => {
      const cached = followCache.get(keyword);
      if (cached !== undefined && cached !== followingRef.current) {
        setFollowing(cached);
      }
      if (cached !== undefined) {
        setChecked(true);
      }
    });
  }, [keyword]);

  // 首次加载时检查是否已关注（去重：共享同一个请求）
  useEffect(() => {
    if (!user) {
      setFollowing(false);
      followCache.delete(keyword);
      setChecked(true);
      return;
    }
    if (followCache.has(keyword)) {
      setFollowing(followCache.get(keyword)!);
      setChecked(true);
      return;
    }
    let cancelled = false;
    fetchAllSubscriptions().then(() => {
      if (cancelled) return;
      const val = followCache.get(keyword) ?? false;
      if (val !== followingRef.current) {
        setFollowing(val);
      }
      setChecked(true);
    });
    return () => { cancelled = true; };
  }, [user, keyword]);

  const toggle = useCallback(async (): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) return false;

    setLoading(true);
    try {
      const isFollowing = followingRef.current;

      if (isFollowing) {
        const res = await fetch(
          `/api/subscriptions?keyword=${encodeURIComponent(keyword)}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.ok) {
          followCache.set(keyword, false);
          setFollowing(false);
          scheduleNotifyListeners();
          return true;
        }
        return false;
      } else {
        try {
          await secureFetch("/api/subscriptions", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify({ keyword }),
          });
          followCache.set(keyword, true);
          setFollowing(true);
          scheduleNotifyListeners();
          return true;
        } catch (err) {
          if (err instanceof Error && err.message.includes("409")) {
            followCache.set(keyword, true);
            setFollowing(true);
            scheduleNotifyListeners();
            return true;
          }
          return false;
        }
      }
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  return { following, toggle, loading: loading || !checked };
}
